// ────────────────────────────────────────────────────────────────────────────
// AdminPrompts — admin dashboard for the prompt archive + unrecognized terms
// ────────────────────────────────────────────────────────────────────────────
//
// Two tabs:
//   1. Submissions (PRI-200) — every user prompt + result metadata. The new
//      canonical archive. Replaces what used to live at /dashboard/admin/prompts
//      as the "unrecognized terms" page (now Tab 2). Filterable + paginated.
//   2. Unrecognized terms — the existing v1 view, untouched. Shows action terms
//      the parser couldn't classify against the SAM3 vocabulary.
// ────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useThemeColors } from '../hooks/useThemeColors'
import {
  getAdminUnrecognizedPrompts,
  getAdminPromptSubmissions,
  getAdminPromptSubmission,
  type UnrecognizedPrompt,
  type PromptSubmission,
  type PromptSubmissionsFilters,
} from '../lib/api'

const ADMIN_EMAILS = ['matt@primateintelligence.ai', 'mehdi@primateintelligence.ai']

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function SimilarityBadge({ similarity }: { similarity: number | null }) {
  if (similarity == null) return <span style={{ color: '#6b7280' }}>—</span>
  const pct = Math.round(similarity * 100)
  const color = pct < 25 ? '#DC2626' : pct < 35 ? '#f59e0b' : '#22c55e'
  return <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', color }}>{pct}%</span>
}

function StatusPill({ status }: { status: PromptSubmission['status'] }) {
  const map: Record<PromptSubmission['status'], { bg: string; fg: string; label: string }> = {
    submitted: { bg: 'rgba(96,165,250,0.12)', fg: '#60A5FA', label: 'submitted' },
    completed: { bg: 'rgba(34,197,94,0.12)', fg: '#22c55e', label: 'completed' },
    failed:    { bg: 'rgba(220,38,38,0.12)', fg: '#DC2626', label: 'failed' },
    rejected:  { bg: 'rgba(217,119,6,0.12)', fg: '#f59e0b', label: 'rejected' },
    cancelled: { bg: 'rgba(123,132,153,0.12)', fg: '#9BA4B5', label: 'cancelled' },
  }
  const m = map[status]
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      background: m.bg, color: m.fg, fontFamily: 'JetBrains Mono, monospace',
      fontSize: 11, fontWeight: 600,
    }}>{m.label}</span>
  )
}

function SourceKindPill({ kind }: { kind: PromptSubmission['source_kind'] }) {
  const map = {
    file_upload:     { bg: 'rgba(124,58,237,0.12)', fg: '#A78BFA', label: 'file' },
    live_stream:     { bg: 'rgba(27,94,255,0.12)',  fg: '#60A5FA', label: 'stream' },
    recorded_stream: { bg: 'rgba(249,115,22,0.12)', fg: '#FB923C', label: 'recorded' },
  }
  const m = map[kind]
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      background: m.bg, color: m.fg, fontFamily: 'JetBrains Mono, monospace',
      fontSize: 11, fontWeight: 600,
    }}>{m.label}</span>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Tab 1 — Submissions (PRI-200)
// ────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50

function SubmissionsTab() {
  const c = useThemeColors()
  const [entries, setEntries] = useState<PromptSubmission[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<PromptSubmission | null>(null)
  const [filters, setFilters] = useState<PromptSubmissionsFilters>({ limit: PAGE_SIZE, offset: 0 })

  const load = useCallback(async (f: PromptSubmissionsFilters) => {
    setLoading(true)
    setError(null)
    try {
      const data = await getAdminPromptSubmissions(f)
      setEntries(data.entries)
      setTotal(data.total)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load(filters) }, [filters, load])

  function applyFilter(patch: Partial<PromptSubmissionsFilters>) {
    setOffset(0)
    setFilters(f => ({ ...f, ...patch, offset: 0 }))
  }

  function nextPage() {
    const next = offset + PAGE_SIZE
    if (next >= total) return
    setOffset(next)
    setFilters(f => ({ ...f, offset: next }))
  }
  function prevPage() {
    const prev = Math.max(0, offset - PAGE_SIZE)
    setOffset(prev)
    setFilters(f => ({ ...f, offset: prev }))
  }

  async function openDetail(id: string) {
    try {
      const full = await getAdminPromptSubmission(id)
      setSelected(full)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load detail')
    }
  }

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search prompt text…"
          onChange={e => applyFilter({ q: e.target.value || undefined })}
          style={{
            flex: '1 1 240px', padding: '8px 12px', borderRadius: 4,
            border: `1px solid ${c.carbon}`, background: c.bgSecondary, color: c.textPrimary,
            fontFamily: 'Inter, sans-serif', fontSize: 13,
          }}
        />
        <select
          onChange={e => applyFilter({ source_kind: (e.target.value || undefined) as PromptSubmissionsFilters['source_kind'] })}
          style={{
            padding: '8px 12px', borderRadius: 4, border: `1px solid ${c.carbon}`,
            background: c.bgSecondary, color: c.textPrimary, fontSize: 13,
          }}
        >
          <option value="">All sources</option>
          <option value="file_upload">File upload</option>
          <option value="live_stream">Live stream</option>
          <option value="recorded_stream">Recorded stream</option>
        </select>
        <select
          onChange={e => applyFilter({ status: (e.target.value || undefined) as PromptSubmissionsFilters['status'] })}
          style={{
            padding: '8px 12px', borderRadius: 4, border: `1px solid ${c.carbon}`,
            background: c.bgSecondary, color: c.textPrimary, fontSize: 13,
          }}
        >
          <option value="">All statuses</option>
          <option value="submitted">Submitted</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button
          onClick={() => void load(filters)}
          style={{
            padding: '8px 16px', borderRadius: 4, border: `1px solid ${c.carbon}`,
            background: 'transparent', color: c.textMuted, fontSize: 13, cursor: 'pointer',
          }}
        >Refresh</button>
      </div>

      {error && (
        <div style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 3, padding: 12, marginBottom: 16 }}>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#DC2626', margin: 0 }}>{error}</p>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: c.textMuted, fontFamily: 'Inter, sans-serif', fontSize: 14 }}>Loading…</div>
      ) : entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: c.textMuted, fontFamily: 'Inter, sans-serif', fontSize: 14 }}>
          No submissions yet. Captures begin as soon as friendly testers start using the product.
        </div>
      ) : (
        <>
          <div style={{ background: c.bgSecondary, border: `1px solid ${c.carbon}`, borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${c.carbon}` }}>
                  {['Submitted', 'User', 'Source', 'Prompt', 'Status', 'Updated?', ''].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: c.textMuted, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((e, idx) => (
                  <tr
                    key={e.id}
                    style={{ borderBottom: idx < entries.length - 1 ? `1px solid ${c.carbon}` : 'none', cursor: 'pointer' }}
                    onClick={() => void openDetail(e.id)}
                  >
                    <td style={{ padding: '12px 16px', fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: c.textMuted, whiteSpace: 'nowrap' }}>{formatDate(e.submitted_at)}</td>
                    <td style={{ padding: '12px 16px', fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: c.textPrimary }}>
                      {e.user_id ? e.user_id.slice(0, 8) : <span style={{ color: c.textMuted }}>anon</span>}
                    </td>
                    <td style={{ padding: '12px 16px' }}><SourceKindPill kind={e.source_kind} /></td>
                    <td style={{ padding: '12px 16px', fontFamily: 'Inter, sans-serif', fontSize: 13, color: c.textPrimary, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.prompt_text}
                    </td>
                    <td style={{ padding: '12px 16px' }}><StatusPill status={e.status} /></td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: e.is_prompt_update ? c.textPrimary : c.textMuted }}>
                      {e.is_prompt_update ? 'mid-session' : '—'}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: c.textMuted }}>›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, fontFamily: 'Inter, sans-serif', fontSize: 13, color: c.textMuted }}>
            <span>Showing {offset + 1}–{Math.min(offset + entries.length, total)} of {total}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={prevPage}
                disabled={offset === 0}
                style={{
                  padding: '6px 12px', borderRadius: 4, border: `1px solid ${c.carbon}`,
                  background: 'transparent', color: offset === 0 ? c.textMuted : c.textPrimary,
                  cursor: offset === 0 ? 'default' : 'pointer', fontSize: 12, opacity: offset === 0 ? 0.4 : 1,
                }}
              >← Previous</button>
              <button
                onClick={nextPage}
                disabled={offset + PAGE_SIZE >= total}
                style={{
                  padding: '6px 12px', borderRadius: 4, border: `1px solid ${c.carbon}`,
                  background: 'transparent', color: (offset + PAGE_SIZE >= total) ? c.textMuted : c.textPrimary,
                  cursor: (offset + PAGE_SIZE >= total) ? 'default' : 'pointer', fontSize: 12, opacity: (offset + PAGE_SIZE >= total) ? 0.4 : 1,
                }}
              >Next →</button>
            </div>
          </div>
        </>
      )}

      {/* Detail drawer */}
      {selected && (
        <div
          onClick={() => setSelected(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', justifyContent: 'flex-end', zIndex: 1000,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '600px', maxWidth: '90vw', height: '100vh', overflowY: 'auto',
              background: c.bgPrimary, borderLeft: `1px solid ${c.carbon}`, padding: 24,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <h2 style={{ fontFamily: 'Inter, sans-serif', fontSize: 18, fontWeight: 600, color: c.textPrimary, margin: 0 }}>
                Submission detail
              </h2>
              <button
                onClick={() => setSelected(null)}
                style={{ background: 'transparent', border: 'none', color: c.textMuted, fontSize: 24, cursor: 'pointer', padding: 0, lineHeight: 1 }}
              >×</button>
            </div>

            <dl style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px 16px', fontSize: 12, fontFamily: 'Inter, sans-serif' }}>
              <dt style={{ color: c.textMuted }}>id</dt>
              <dd style={{ color: c.textPrimary, fontFamily: 'JetBrains Mono, monospace', margin: 0 }}>{selected.id}</dd>

              <dt style={{ color: c.textMuted }}>submitted</dt>
              <dd style={{ color: c.textPrimary, margin: 0 }}>{formatDate(selected.submitted_at)}</dd>

              <dt style={{ color: c.textMuted }}>completed</dt>
              <dd style={{ color: c.textPrimary, margin: 0 }}>{selected.completed_at ? formatDate(selected.completed_at) : '—'}</dd>

              <dt style={{ color: c.textMuted }}>status</dt>
              <dd style={{ margin: 0 }}><StatusPill status={selected.status} /></dd>

              <dt style={{ color: c.textMuted }}>source</dt>
              <dd style={{ margin: 0 }}><SourceKindPill kind={selected.source_kind} /></dd>

              <dt style={{ color: c.textMuted }}>user</dt>
              <dd style={{ color: c.textPrimary, fontFamily: 'JetBrains Mono, monospace', margin: 0 }}>
                {selected.user_id ?? <span style={{ color: c.textMuted }}>anonymous (hash: {selected.anon_ip_hash ? selected.anon_ip_hash.slice(0, 16) + '…' : '—'})</span>}
              </dd>

              <dt style={{ color: c.textMuted }}>session_id</dt>
              <dd style={{ color: c.textPrimary, fontFamily: 'JetBrains Mono, monospace', margin: 0 }}>{selected.session_id ?? '—'}</dd>

              <dt style={{ color: c.textMuted }}>job_id</dt>
              <dd style={{ color: c.textPrimary, fontFamily: 'JetBrains Mono, monospace', margin: 0 }}>{selected.job_id ?? '—'}</dd>

              <dt style={{ color: c.textMuted }}>prompt update?</dt>
              <dd style={{ color: c.textPrimary, margin: 0 }}>{selected.is_prompt_update ? 'yes — mid-session' : 'no — initial'}</dd>

              <dt style={{ color: c.textMuted, gridColumn: '1 / 3', marginTop: 8 }}>prompt</dt>
              <dd style={{ gridColumn: '1 / 3', margin: 0 }}>
                <pre style={{ background: c.bgSecondary, border: `1px solid ${c.carbon}`, borderRadius: 4, padding: 12, fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: c.textPrimary, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                  {selected.prompt_text}
                </pre>
              </dd>

              {selected.parsed_intent && (
                <>
                  <dt style={{ color: c.textMuted, gridColumn: '1 / 3', marginTop: 8 }}>parsed intent</dt>
                  <dd style={{ gridColumn: '1 / 3', margin: 0 }}>
                    <pre style={{ background: c.bgSecondary, border: `1px solid ${c.carbon}`, borderRadius: 4, padding: 12, fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: c.textPrimary, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, maxHeight: 200, overflowY: 'auto' }}>
                      {JSON.stringify(selected.parsed_intent, null, 2)}
                    </pre>
                  </dd>
                </>
              )}

              {selected.result_metadata && (
                <>
                  <dt style={{ color: c.textMuted, gridColumn: '1 / 3', marginTop: 8 }}>result metadata</dt>
                  <dd style={{ gridColumn: '1 / 3', margin: 0 }}>
                    <pre style={{ background: c.bgSecondary, border: `1px solid ${c.carbon}`, borderRadius: 4, padding: 12, fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: c.textPrimary, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, maxHeight: 300, overflowY: 'auto' }}>
                      {JSON.stringify(selected.result_metadata, null, 2)}
                    </pre>
                  </dd>
                </>
              )}

              {selected.rejection_reason && (
                <>
                  <dt style={{ color: c.textMuted, gridColumn: '1 / 3', marginTop: 8 }}>rejection reason</dt>
                  <dd style={{ gridColumn: '1 / 3', margin: 0, color: '#DC2626', fontSize: 13 }}>{selected.rejection_reason}</dd>
                </>
              )}
            </dl>
          </div>
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Tab 2 — Unrecognized terms (existing v1 view, preserved)
// ────────────────────────────────────────────────────────────────────────────

function UnrecognizedTermsTab() {
  const c = useThemeColors()
  const [prompts, setPrompts] = useState<UnrecognizedPrompt[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await getAdminUnrecognizedPrompts()
      setPrompts(data.entries)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: c.textMuted, margin: 0 }}>
          Action terms the model couldn&rsquo;t match to known classes.
        </p>
        <button
          onClick={() => void load()}
          style={{ background: 'transparent', border: `1px solid ${c.carbon}`, borderRadius: 3, padding: '8px 16px', fontFamily: 'Inter, sans-serif', fontSize: 13, color: c.textMuted, cursor: 'pointer' }}
        >Refresh</button>
      </div>

      {error && (
        <div style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 3, padding: 12, marginBottom: 16 }}>
          <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: '#DC2626', margin: 0 }}>{error}</p>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: c.textMuted, fontFamily: 'Inter, sans-serif', fontSize: 14 }}>Loading…</div>
      ) : prompts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: c.textMuted, fontFamily: 'Inter, sans-serif', fontSize: 14 }}>No unrecognized prompts yet.</div>
      ) : (
        <div style={{ background: c.bgSecondary, border: `1px solid ${c.carbon}`, borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${c.carbon}` }}>
                {['Term', 'Nearest Match', 'Similarity', 'Times Seen', 'First Seen'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontFamily: 'Inter, sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: c.textMuted, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {prompts.map((p, idx) => (
                <tr key={p.id} style={{ borderBottom: idx < prompts.length - 1 ? `1px solid ${c.carbon}` : 'none' }}>
                  <td style={{ padding: '12px 16px', fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: c.textPrimary }}>{p.original_term}</td>
                  <td style={{ padding: '12px 16px', fontFamily: 'Inter, sans-serif', fontSize: 13, color: p.nearest_match ? c.textPrimary : c.textMuted }}>{p.nearest_match ?? '—'}</td>
                  <td style={{ padding: '12px 16px' }}><SimilarityBadge similarity={p.similarity} /></td>
                  <td style={{ padding: '12px 16px', fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: c.textPrimary, fontWeight: p.count > 1 ? 700 : 400 }}>{p.count}</td>
                  <td style={{ padding: '12px 16px', fontFamily: 'Inter, sans-serif', fontSize: 13, color: c.textMuted }}>{new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Page shell with tab nav
// ────────────────────────────────────────────────────────────────────────────

type Tab = 'submissions' | 'unrecognized'

export default function AdminPrompts() {
  const { user, isLoaded } = useUser()
  const c = useThemeColors()
  const [tab, setTab] = useState<Tab>('submissions')

  const isAdmin = isLoaded && user != null && ADMIN_EMAILS.includes(user.primaryEmailAddress?.emailAddress ?? '')

  if (!isLoaded) return <div className="min-h-screen bg-void" />

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-void text-chalk flex items-center justify-center">
        <p className="text-body text-steel">Access denied</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-void text-chalk px-6 py-8" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 24, color: c.textPrimary, margin: 0 }}>Prompts</h1>
        <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, color: c.textMuted, marginTop: 4 }}>
          Every user prompt + result we&rsquo;ve captured, plus the legacy unrecognized-terms view.
        </p>
      </div>

      {/* Tab nav */}
      <div role="tablist" style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${c.carbon}`, marginBottom: 24 }}>
        {([
          { id: 'submissions',  label: 'Submissions' },
          { id: 'unrecognized', label: 'Unrecognized terms' },
        ] as Array<{ id: Tab; label: string }>).map(t => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '10px 18px', background: 'transparent', border: 'none',
              borderBottom: `2px solid ${tab === t.id ? '#2058F0' : 'transparent'}`,
              color: tab === t.id ? c.textPrimary : c.textMuted,
              cursor: 'pointer', fontFamily: 'Inter, sans-serif', fontSize: 14,
              fontWeight: tab === t.id ? 600 : 400,
              marginBottom: -1,
            }}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'submissions' && <SubmissionsTab />}
      {tab === 'unrecognized' && <UnrecognizedTermsTab />}
    </div>
  )
}
